import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { cartQueue } from '@theme/cart-queue';
import {
  fetchCartAttributes,
  hasStoreCartAttributes,
  loadStoreCatalog,
} from '@theme/store-pricing';
import {
  formatStoreInventoryCartQuantityError,
  getStoreInventoryConfig,
  readContinueSellingSignalsFromElement,
  resolveRevertedCartQuantity,
  validateCartLineQuantity,
} from '@theme/store-inventory';
import { CartCheckoutValidator } from '@theme/cart-checkout-validator';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLButtonElement} [checkoutButton]
 * @property {HTMLElement} [checkoutBlockedMessage]
 * @property {HTMLElement} [checkoutBlockedMessageText]
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {AbortController | null} */
  #cartLifecycleAbortController = null;

  /** @type {CartCheckoutValidator | null} */
  #checkoutValidator = null;

  connectedCallback() {
    super.connectedCallback();

    this.#cartLifecycleAbortController = new AbortController();
    const { signal } = this.#cartLifecycleAbortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#checkoutValidator = new CartCheckoutValidator({
      root: this,
      clearCartLineError: (line) => this.#clearCartLineError(line),
    });
    this.#checkoutValidator.connect(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#cartLifecycleAbortController?.abort();
    this.#cartLifecycleAbortController = null;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    void this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      this.#checkoutValidator?.resetCheckoutPresentation();

      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    const revalidateAfterLineRemoval = () => {
      this.updatedCallback();
      requestAnimationFrame(() => {
        this.#checkoutValidator?.validateNow();
      });
    };

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => {
        row.remove();
        revalidateAfterLineRemoval();
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const { line, quantity } = config;

    this.#clearCartLineError(line);

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    try {
      const lineItemRow = this.refs.cartItemRows[line - 1];
      const variantId = lineItemRow instanceof HTMLElement ? lineItemRow.dataset.variantId?.trim() : '';

      const { baseUrl, authToken, threshold } = getStoreInventoryConfig();
      // Availabilities JSON is from cart section render; may be stale until cart re-renders after variant/store changes.
      const storeLocatorAvailabilities =
        lineItemRow instanceof HTMLElement
          ? lineItemRow.dataset.storeLocatorAvailabilities?.trim() ?? ''
          : '';
      const hasShopifyAvailabilities = storeLocatorAvailabilities.length > 0;
      const continueSignals =
        lineItemRow instanceof HTMLElement ? readContinueSellingSignalsFromElement(lineItemRow) : { inventoryPolicy: '', inStockInStoreValues: [] };
      const hasContinueSignals =
        continueSignals.inventoryPolicy === 'continue' || continueSignals.inStockInStoreValues.length > 0;

      if ((baseUrl || hasShopifyAvailabilities || hasContinueSignals) && variantId && quantity > 0) {
        const [cartAttributes, catalog] = await Promise.all([
          fetchCartAttributes(),
          Promise.resolve(loadStoreCatalog()),
        ]);

        if (hasStoreCartAttributes(cartAttributes)) {
          const validation = await validateCartLineQuantity({
            baseUrl,
            authToken,
            variantId,
            cartAttributes,
            catalog,
            requestedQuantity: quantity,
            threshold,
            storeLocatorAvailabilities: hasShopifyAvailabilities ? storeLocatorAvailabilities : undefined,
            inventoryPolicy: continueSignals.inventoryPolicy,
            inStockInStoreValues: continueSignals.inStockInStoreValues,
          });

          if (!validation.ok) {
            const quantitySelector = this.refs.quantitySelectors[line - 1];
            const quantityInput = quantitySelector?.querySelector('input');
            const previousQuantity = quantityInput
              ? Number(quantityInput.defaultValue) || quantity
              : quantity;
            const revertedQuantity = resolveRevertedCartQuantity(
              quantity,
              validation.availableQuantity,
              previousQuantity
            );
            const message = formatStoreInventoryCartQuantityError({
              availableQuantity: validation.availableQuantity,
              revertedQuantity,
              requestedQuantity: quantity,
            });
            this.#handleCartError(line, { errors: message, revertedQuantity });
            this.#resetLineShimmer(line);
            this.#checkoutValidator?.scheduleValidation();
            return;
          }
        }
      }

      this.#disableCartItems();
      const { cartTotal } = this.refs;

      this.#shimmerLine(line);
      cartTotal?.shimmer();

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const lineItemKey = lineItemRow instanceof HTMLElement ? lineItemRow.dataset.key?.trim() : undefined;

      const body = JSON.stringify({
        ...(lineItemKey ? { id: lineItemKey } : { line }),
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const parsedResponseText = await cartQueue.enqueue(async () => {
        const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        const responseText = await response.text();
        return JSON.parse(responseText);
      });

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        this.#checkoutValidator?.validateNow();
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.#updateQuantitySelectors(parsedResponseText);

      await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
        mode: this.isDrawer ? 'hydration' : 'full',
      });

      this.updatedCallback();

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      this.#clearCartLineError(line);
      this.#updateCartQuantitySelectorButtonStates();
      await this.#validateCheckoutAfterCartMorph();
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      this.#checkoutValidator?.validateNow();
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * @param {number} line
   */
  #shimmerLine(line) {
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * @param {number} line
   */
  #resetLineShimmer(line) {
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (lineItemRow) resetShimmer(lineItemRow);

    const { cartTotal } = this.refs;
    if (cartTotal) resetShimmer(cartTotal);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * @param {number} line
   */
  #clearCartLineError(line) {
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (!(cartItemErrorContainer instanceof HTMLElement)) return;
    if (cartItemErrorContainer.dataset.checkoutWarning === 'true') return;

    cartItemErrorContainer.classList.add('hidden');
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   * @param {number} [parsedResponseText.revertedQuantity]
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    const revertedValue =
      parsedResponseText.revertedQuantity != null
        ? String(parsedResponseText.revertedQuantity)
        : quantityInput.defaultValue;

    quantityInput.value = revertedValue;
    quantityInput.defaultValue = revertedValue;

    const cartQuantitySelector = quantitySelector?.closest('cart-quantity-selector-component');
    if (cartQuantitySelector && 'updateButtonStates' in cartQuantitySelector) {
      /** @type {{ updateButtonStates?: () => void }} */ (cartQuantitySelector).updateButtonStates?.();
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemErrorContainer.removeAttribute('data-checkout-warning');
    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.#resetLineShimmer(line);
  };

  /**
   * Refresh checkout presentation after cart section morphs update refs/DOM.
   */
  async #validateCheckoutAfterCartMorph() {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.#checkoutValidator?.validateNow();
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      this.#renderCartSectionThenValidate();
      return;
    }
    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    if (event.target === this) return;

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml).then(() => {
        this.updatedCallback();
        this.#updateCartQuantitySelectorButtonStates();
        void this.#validateCheckoutAfterCartMorph();
      });
    } else {
      this.#renderCartSectionThenValidate();
    }
  };

  /**
   * Store-locator cart updates omit section HTML; validate only after the cart section morph finishes.
   */
  #renderCartSectionThenValidate() {
    void sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
      this.updatedCallback();
      void this.#validateCheckoutAfterCartMorph();
    });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
